/**
 * 브라우저(데스크톱) 알림.
 *
 * 앱 내 종 아이콘·토스트는 화면을 보고 있어야 눈에 들어온다.
 * 결재 차례가 돌아왔거나 문서가 반려된 것처럼 바로 알아야 하는 일은
 * 다른 창을 보고 있어도 뜨도록 OS 알림으로 한 번 더 띄운다.
 *
 * 켜고 끄는 값은 이 기기에만 저장한다(사람이 아니라 기기 단위 설정이다).
 * 회사 PC 에서는 켜고 공용 PC 에서는 끄는 식으로 쓸 수 있다.
 */

const STORAGE_KEY = 'workncrm.desktopNotification';

/** 브라우저가 알림을 지원하는가. (http 로 접속해도 동작하지만 일부 브라우저는 https 를 요구한다) */
export const isDesktopNotificationSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export const getDesktopNotificationPermission = () =>
  (isDesktopNotificationSupported() ? Notification.permission : 'unsupported');

/** 사용자가 이 기기에서 켜 두었는가. 기본은 꺼짐(원하지 않는 사람에게 갑자기 뜨지 않게). */
export const isDesktopNotificationEnabled = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    // 사생활 보호 모드 등에서 localStorage 가 막힐 수 있다.
    return false;
  }
};

export const setDesktopNotificationEnabled = (enabled) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* 저장이 막혀도 이번 세션 동작에는 지장이 없다 */
  }
};

/**
 * 알림 권한을 요청한다.
 * 브라우저가 사용자 조작(클릭) 안에서만 허용하므로 반드시 버튼 등에서 부른다.
 */
export const requestDesktopNotificationPermission = async () => {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;

  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
};

/**
 * 알림을 띄운다.
 * 클릭하면 창을 앞으로 가져오고 targetUrl 로 이동하도록 이벤트를 쏜다.
 * (여기서 직접 화면을 옮기면 라우터를 거치지 않아 전체 새로고침이 된다)
 */
export const showDesktopNotification = ({ title, body, tag, targetUrl }) => {
  if (!isDesktopNotificationSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  if (!isDesktopNotificationEnabled()) return false;

  /*
   * 지금 이 화면을 실제로 보고 있는 중이면 토스트로 이미 보이므로 두 번 띄우지 않는다.
   * 탭이 보이더라도 창이 뒤에 있으면(다른 프로그램을 쓰는 중) 눈에 안 들어오므로 띄운다.
   */
  const looking = typeof document !== 'undefined'
    && document.visibilityState === 'visible'
    && (typeof document.hasFocus !== 'function' || document.hasFocus());
  if (looking) return false;

  try {
    const notification = new Notification(title, {
      body,
      // 같은 tag 는 덮어쓰기 되어 같은 알림이 여러 개 쌓이지 않는다.
      tag: tag || 'workncrm',
      icon: '/favicon.svg',
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (targetUrl) {
        window.dispatchEvent(new CustomEvent('desktopNotificationClick', { detail: { targetUrl } }));
      }
    };

    return true;
  } catch {
    // 알림을 못 띄워도 앱 동작에는 영향이 없어야 한다.
    return false;
  }
};

/**
 * 데스크톱 알림으로 띄울 알림인지 판단한다.
 *
 * "내 문서·내 차례에 결론이 난" 것만 띄운다. 대상은 이미 서버에서 걸러져 오므로 종류만 본다.
 *   - [결재요청] : 내 결재 차례가 되었을 때 (현재 결재자에게만 발송)
 *   - [반려]     : 내 기안 문서가 반려되었을 때 (기안자에게만 발송)
 *   - [결재완료] : 내 기안 문서의 결재가 모두 끝났을 때 (기안자에게만 발송)
 *   - [일정]     : 내 일정이 곧 시작할 때 (등록자·참여자에게만 발송)
 *   - [업무]     : 내가 맡은 업무의 종료가 다가올 때 (담당자에게만 발송)
 *   - [자원예약] : 내가 잡은 자원 사용 시각이 다가올 때 (신청자에게만 발송)
 *   - [코멘트]   : 내 업무에 코멘트가 달렸을 때 (담당자에게만 발송)
 *
 * [참조] 는 뺀다 — 남의 문서 소식이라 급히 알 필요가 없고, 수가 많아 방해가 된다.
 */
const DESKTOP_NOTIFICATION_PREFIXES = [
  '[결재요청]', '[반려]', '[결재완료]', '[일정]', '[업무]', '[자원예약]', '[코멘트]',
];

export const shouldShowOnDesktop = (message) =>
  typeof message === 'string'
  && DESKTOP_NOTIFICATION_PREFIXES.some((prefix) => message.startsWith(prefix));

/** 알림 문구에서 제목과 본문을 나눈다. "[결재요청] 휴가신청 (홍길동)" → 제목/본문 */
export const splitNotificationMessage = (message) => {
  const matched = /^\[([^\]]+)\]\s*(.*)$/.exec(message || '');
  if (!matched) return { title: '알림', body: message || '' };
  return { title: matched[1], body: matched[2] || message };
};
